import type { Definition } from '@exodus/dependency-types';
import createFetchival from '@exodus/fetch/experimental/create-fetchival';
import { WalletAccount } from '@exodus/models';

import type { PlaygroundSbt, PlaygroundSbtAtom } from '../types.js';

type Dependencies = {
  fetch: typeof fetch;
  playgroundSbtAtom: PlaygroundSbtAtom;
  addressProvider: any;
  config: { baseUrl: string };
};

export class PlaygroundSbtModule {
  #fetchival: any;
  #playgroundSbtAtom: PlaygroundSbtAtom;
  #addressProvider: any;
  #baseUrl: string;

  constructor({
    fetch,
    playgroundSbtAtom,
    addressProvider,
    config,
  }: Dependencies) {
    this.#playgroundSbtAtom = playgroundSbtAtom;
    this.#addressProvider = addressProvider;
    this.#fetchival = createFetchival({ fetch });
    this.#baseUrl = config.baseUrl;
  }

  #fetch = async (address: string): Promise<PlaygroundSbt | null> => {
    const url = new URL(`${this.#baseUrl}/tokens`);
    const response = await this.#fetchival(url).get({ address });
    return response.soulboundToken;
  };

  #getEthereumAddress = async (): Promise<string> => {
    const { address } = await this.#addressProvider.getReceiveAddress({
      assetName: 'ethereum',
      walletAccount: WalletAccount.DEFAULT_NAME,
    });

    return address;
  };

  refresh = async () => {
    try {
      const address = await this.#getEthereumAddress();
      const playgroundSbt = await this.#fetch(address);
      await this.#playgroundSbtAtom.set(playgroundSbt);
      return { success: true };
    } catch {
      return { success: false };
    }
  };
}

const createPlaygroundSbtModule = (args: Dependencies) =>
  new PlaygroundSbtModule(args);

const playgroundSbtModuleDefinition = {
  id: 'playgroundSbt',
  type: 'module',
  factory: createPlaygroundSbtModule,
  dependencies: ['fetch', 'addressProvider', 'playgroundSbtAtom', 'config'],
} as const satisfies Definition;

export default playgroundSbtModuleDefinition;
